import type {
  PipelineHistoryField,
  PipelineHistoryFieldChange,
} from '@repo/lakehouse-sdk';

import { namedColumnChanges } from './historyColumns';
import { historyChangeKind, historyPathLabel } from './historyView';

export interface HistoryChangeRow {
  path: string;
  label: string;
  context?: string;
  kind: 'Added' | 'Removed' | 'Modified';
  changes: PipelineHistoryFieldChange[];
}

export const historyChangeRows = (
  changes: PipelineHistoryFieldChange[],
  snapshot: PipelineHistoryField[]
): HistoryChangeRow[] => {
  const normalized = namedColumnChanges(changes, snapshot);
  changes = normalized.changes;
  snapshot = normalized.snapshot;
  const fields = new Map(snapshot.map((field) => [field.path, field]));
  const arrays = new Set(
    [...snapshot, ...changes.flatMap((change) => [change.before, change.after])]
      .filter((field) => field?.kind === 'ARRAY')
      .map((field) => field?.path)
  );
  const groups = new Map<string, PipelineHistoryFieldChange[]>();
  for (const change of changes) {
    const parentPath = change.path.slice(0, change.path.lastIndexOf('/'));
    if ((!change.before || !change.after) && !arrays.has(parentPath)) {
      const field = change.before ?? change.after;
      if (
        field?.kind === 'NULL' ||
        (field?.kind === 'STRING' && field.value === '') ||
        (field?.kind === 'NUMBER' && field.value === '0')
      ) {
        continue;
      }
    }
    const segments = change.path.split('/');
    let itemPath = change.path;
    // Only typed arrays qualify: numeric object keys remain ordinary settings.
    for (let index = 1; index < segments.length; index++) {
      if (
        (/^(0|[1-9]\d*)$/.test(segments[index]) ||
          normalized.names.has(segments.slice(0, index + 1).join('/'))) &&
        arrays.has(segments.slice(0, index).join('/'))
      ) {
        itemPath = segments.slice(0, index + 1).join('/');
        break;
      }
    }
    const group = groups.get(itemPath) ?? [];
    group.push(change);
    groups.set(itemPath, group);
  }
  return Array.from(groups, ([path, itemChanges]) => {
    const rootChange = itemChanges.find((change) => change.path === path);
    const namePath = `${path}/name`;
    const nameChange = itemChanges.find((change) => change.path === namePath);
    const parent = path.slice(0, path.lastIndexOf('/'));
    const isItem = arrays.has(parent);
    const name = isItem
      ? (nameChange?.after ?? nameChange?.before ?? fields.get(namePath))
      : undefined;
    const columnName = normalized.names.get(path);
    return {
      path,
      label:
        columnName ??
        (name?.kind === 'STRING' && name.value
          ? name.value
          : historyPathLabel(path)),
      context: columnName ? historyPathLabel(parent) : undefined,
      kind: rootChange ? historyChangeKind(rootChange) : 'Modified',
      changes: itemChanges,
    };
  });
};

export const historyChangeSide = (
  row: HistoryChangeRow,
  side: 'before' | 'after'
): PipelineHistoryField[] => {
  const fields = row.changes.flatMap((change) =>
    change[side] ? [change[side]] : []
  );
  const parents = new Set<string>();
  for (const field of fields) {
    let path = field.path;
    while (path.includes('/')) {
      path = path.slice(0, path.lastIndexOf('/'));
      parents.add(path);
    }
  }
  const typeChanges = new Set(
    row.changes
      .filter(
        (change) =>
          change.before &&
          change.after &&
          change.before.kind !== change.after.kind
      )
      .map((change) => change.path)
  );
  return fields.filter(
    (field) =>
      !(
        (field.kind === 'OBJECT' || field.kind === 'ARRAY') &&
        parents.has(field.path) &&
        !typeChanges.has(field.path)
      )
  );
};
